import { ErrorMessage, Formik, FormikHelpers } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { apiConfiguration } from "../../api-configuration";
import { ErrorsListCard } from "../../components/forms/errors-list";
import { TextFieldFormGroup } from "../../components/forms/groups/text-field-group";
import { logError } from "../../errors";
import { MemorialPageApi, MemorialPageWebForm, SiteDto } from "../../lib-api";
import { SubmitState } from "../../util/loading";
import { Schema, array, bool, object, string } from "yup";
import { AssetUpload } from "../../components/forms/asset-upload";
import { getObject } from "../../util/local-storage";
import { SaveData } from "../../components/forms/save-data";
import { assetValidationSchema } from "../user-article/validation";
import Select from "react-select";

interface StorageModel {
  values: MemorialPageWebForm;
  state: FormState;
}

interface FormState {
  errors?: { [key: string]: string };
  state: SubmitState;
}

const api = new MemorialPageApi(apiConfiguration);
const storageKey = "memorial-page-draft";

function getInitialState(savedState?: FormState): FormState {
  return {
    errors: savedState?.errors ?? undefined,
    state: savedState?.state ?? SubmitState.None,
  };
}

export const validationSchema: Schema<MemorialPageWebForm> = object().shape({
  authorName: string()
    .required("Dit navn er påkrævet")
    .max(200, "Maksimalt 200 tegn"),
  authorEmail: string()
    .email("Indtast en gyldig email")
    .required("Din email er påkrævet")
    .max(200, "Maksimalt 200 tegn"),
  rightsToAssets: bool()
    .required()
    .oneOf([true], "Du skal godkende at du har rettighederne til billederne"),
  assets: array()
    .label("Billede")
    .required()
    .of(assetValidationSchema)
    .min(1)
    .max(1),
  primarySiteId: string().required("Vælg primær side"),
  secondarySiteIds: array().of(string().required()).label("Vælg sider"),
  deceasedName: string()
    .required("Modtagers navn er påkrævet")
    .max(200, "Maksimalt 200 tegn"),
  bornYear: string().required("Indtast fødselsår"),
  deceasedYear: string().required("Indtast dødsår"),
  quote: string(),
});

export const MemorialPage: React.FC<{
  csrf: string;
  siteId: string;
}> = ({ csrf, siteId }) => {
  const [savedData] = useState(getObject<StorageModel>(storageKey));
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formState, setFormState] = useState<FormState>(
    getInitialState(savedData?.state)
  );
  const recaptchaRef = useRef<ReCAPTCHA>();
  const [sites, SetSites] = useState<SiteDto[]>([]);

  useEffect(() => {
    (async () => {
      const res = await api.getMemorialPageSiteDtos();
      SetSites(res);
    })();
  }, [api]);

  function getInitialValues(savedValues?: MemorialPageWebForm) {
    return {
      authorName: savedValues?.authorName ?? "",
      authorEmail: savedValues?.authorEmail ?? "",
      rightsToAssets: savedValues?.rightsToAssets ?? false,
      assets: savedValues?.assets ?? [],
      primarySiteId: siteId,
      secondarySiteIds: savedValues?.secondarySiteIds ?? [],
      imageAssetFile: savedValues?.imageAssetFile ?? null,
      deceasedName: savedValues?.deceasedName ?? "",
      bornYear: savedValues?.bornYear ?? "",
      deceasedYear: savedValues?.deceasedYear ?? "",
      quote: savedValues?.quote ?? "",
    };
  }

  const handleSubmit = useCallback(
    async (
      values: MemorialPageWebForm,
      helpers: FormikHelpers<MemorialPageWebForm>
    ) => {
      try {
        setFormState({
          state: SubmitState.None,
        });
        await api
          .withMiddleware({
            pre: async (context) => ({
              ...context,
              init: {
                ...context.init,
                headers: {
                  ...context.init.headers,
                  RequestVerificationToken: csrf,
                },
              },
            }),
          })
          .createMemorialPage({
            memorialPageWebForm: { ...values, primarySiteId: siteId },
          });

        setFormState({
          state: SubmitState.Submitted,
        });
      } catch (e) {
        if (e instanceof Response && e.status == 400) {
          const json = await e.json();

          e = new Error(JSON.stringify(json));

          setFormState({
            state: SubmitState.Error,
            errors: json.errors,
          });
        } else {
          setFormState({
            state: SubmitState.Error,
          });
        }

        if (!(e instanceof Error)) {
          e = new Error(JSON.stringify(e));
        }
        logError(e);
        recaptchaRef.current?.reset();
        helpers.setFieldValue("recaptcha", undefined);
      }
    },
    [recaptchaRef.current, setFormState]
  );

  return (
    <>
      <Formik<MemorialPageWebForm>
        initialValues={getInitialValues(savedData?.values)}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({
          handleChange,
          values,
          touched,
          errors,
          isSubmitting,
          setFieldValue,
          handleSubmit,
          resetForm,
        }) => (
          <>
            <SaveData<StorageModel>
              data={{ values, state: formState }}
              storageKey={storageKey}
            />
            {formState.state === SubmitState.Submitted ? (
              <>
                <h1>Tak for dit bidrag</h1>
                <p>
                  Din mindeside er nu sendt til godkendelse ved redaktionen, og
                  vi vil snarest muligt godkende og offentliggøre din mindeside.
                </p>
                <p>
                  Du får en mail så snart den er godkendt og offentliggjort.
                </p>
                <Button
                  onClick={() => {
                    resetForm({ values: getInitialValues() });
                    setFormState(getInitialState());
                  }}
                  variant="primary"
                >
                  Opret ny mindeside
                </Button>
              </>
            ) : (
              <>
                <h1>Opret mindeside</h1>
                <hr />
                <Form onSubmit={handleSubmit}>
                  <TextFieldFormGroup
                    fieldName="authorName"
                    fieldDisplayName="Dit navn"
                    placeholder="Indtast dit navn..."
                    error={touched.authorName ? errors.authorName : undefined}
                    value={values.authorName}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <TextFieldFormGroup
                    fieldName="authorEmail"
                    fieldDisplayName="Din email"
                    placeholder="Indtast din email..."
                    error={touched.authorEmail ? errors.authorEmail : undefined}
                    value={values.authorEmail ?? undefined}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />

                  <Form.Group>
                    <Form.Label>
                      Vælg øvrige sider (mindesiden vil altid blive oprettet på
                      siden du er på nu)
                    </Form.Label>
                    <Select
                      isMulti
                      name="secondarySiteIds"
                      options={sites.map((site) => ({
                        value: site.id,
                        label: site.name,
                      }))}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      onChange={(selectedOptions) =>
                        setFieldValue(
                          "secondarySiteIds",
                          selectedOptions.map((option) => option.value)
                        )
                      }
                      value={values.secondarySiteIds?.map((id) => ({
                        value: id,
                        label: sites.find((site) => site.id === id)?.name ?? id,
                      }))}
                      isDisabled={isSubmitting}
                      placeholder="Vælg øvrige sider... (ikke påkrævet)"
                    />
                    <ErrorMessage
                      name="secondarySiteIds"
                      component={Form.Text}
                      className="text-danger"
                    />
                  </Form.Group>

                  <TextFieldFormGroup
                    fieldName="deceasedName"
                    fieldDisplayName="Afdødes navn"
                    placeholder="Indtast afdødes navn..."
                    error={
                      touched.deceasedName ? errors.deceasedName : undefined
                    }
                    value={values.deceasedName ?? undefined}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <TextFieldFormGroup
                    fieldName="bornYear"
                    fieldDisplayName="Afdødes fødselsår"
                    placeholder="Indtast afdødes fødselsår..."
                    error={touched.bornYear ? errors.bornYear : undefined}
                    value={values.bornYear ?? undefined}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <TextFieldFormGroup
                    fieldName="deceasedYear"
                    fieldDisplayName="Dødsår"
                    placeholder="Indtast dødsår..."
                    error={
                      touched.deceasedYear ? errors.deceasedYear : undefined
                    }
                    value={values.deceasedYear ?? undefined}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <TextFieldFormGroup
                    fieldName="quote"
                    fieldDisplayName="Citat"
                    placeholder="Indtast citat..."
                    error={touched.quote ? errors.quote : undefined}
                    value={values.quote ?? undefined}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                  />
                  <AssetUpload
                    csrf={csrf}
                    helperText="Billedet må gerne være af den afdøde, på vandret led (bredformat), og i så høj opløsning som muligt"
                    maxAssets={1}
                    rounded={true}
                  />
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      name="rightsToAssets"
                      checked={values.rightsToAssets ?? false}
                      onChange={handleChange}
                      label="Jeg bekræfter, at jeg har brugsret til billedet"
                    />
                    <ErrorMessage
                      name="rightsToAssets"
                      component={Form.Text}
                      className="text-danger"
                    />
                  </Form.Group>
                  <Form.Group>
                    <ReCAPTCHA
                      sitekey="6Lc4304eAAAAAJ99u8pRHC4DS7pZanzszHRW-mWh"
                      onChange={(token) => setFieldValue("recaptcha", token)}
                      ref={recaptchaRef}
                    />
                  </Form.Group>
                  <Button
                    className="align-self-end mt-3"
                    disabled={isSubmitting}
                    variant="primary"
                    block={false}
                    type="submit"
                    size="lg"
                    onClick={() => setSubmitAttempted(true)}
                  >
                    Indsend
                  </Button>
                </Form>
                {submitAttempted && <ErrorsListCard errors={errors} />}
              </>
            )}
            {formState.state == SubmitState.Error && (
              <div>
                {formState.errors ? (
                  <ErrorsListCard
                    title="Vores server meldte følgende fejl"
                    errors={formState.errors}
                  />
                ) : (
                  <>Ups, der opstod en fejl. Prøv evt. at genindlæse siden.</>
                )}
              </div>
            )}
          </>
        )}
      </Formik>
    </>
  );
};
